import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AuditEntry,
  Batch,
  Contact,
  ContactsPerSource,
  DashboardKpis,
  DomainDetail,
  DomainsResponse,
  EmailAccountDetail,
  EmailAccountsListResponse,
  LabelMapping,
  LabelMappingsResponse,
  LabelMappingProvider,
  LabelMappedEvent,
  MailAccountGroupsResponse,
  FormFill,
  HealthSnapshot,
  IngressKey,
  Job,
  JobEvent,
  JobsPerHour,
  OutboundPush,
  ServiceSecret,
  Vm,
  VmHealthPoint,
} from '@/types/api';
import {
  bulkAddToGroup,
  bulkRemoveFromGroup,
  createMailAccountGroup,
  deleteMailAccountGroup,
  detachEmailAccount,
  fetchEmailAccount,
  fetchEmailAccounts,
  fetchMailAccountGroups,
  pauseEmailAccount,
  replaceEmailAccountGroups,
  resumeEmailAccount,
  syncEmailAccounts,
  updateMailAccountGroup,
  type EmailAccountsQuery,
} from '@/lib/api/email-accounts';

const LIVE_INTERVAL = 30_000;

export function useHealth() {
  return useQuery<HealthSnapshot>({
    queryKey: ['health'],
    queryFn: () => api.get<HealthSnapshot>('/v1/health'),
    refetchInterval: 10_000,
  });
}

export function useDashboardKpis() {
  return useQuery<DashboardKpis>({
    queryKey: ['dashboard', 'kpis'],
    queryFn: () => api.get<DashboardKpis>('/v1/dashboard/kpis'),
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useJobsPerHour() {
  return useQuery<JobsPerHour[]>({
    queryKey: ['dashboard', 'jobs-per-hour'],
    queryFn: () => api.get<{ data: JobsPerHour[] }>('/v1/dashboard/jobs-per-hour').then((r) => r.data ?? []),
    refetchInterval: 30_000,
  });
}

export function useContactsPerSource() {
  return useQuery<ContactsPerSource[]>({
    queryKey: ['dashboard', 'contacts-per-source'],
    queryFn: () =>
      api.get<{ data: ContactsPerSource[] }>('/v1/dashboard/contacts-per-source').then((r) => r.data ?? []),
    refetchInterval: 60_000,
  });
}

export function useVms() {
  return useQuery<Vm[]>({
    queryKey: ['vms'],
    queryFn: () => api.get<{ vms: Vm[] }>('/v1/vms').then((r) => r.vms ?? []),
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useVm(id: string | undefined) {
  return useQuery<Vm>({
    queryKey: ['vms', id],
    queryFn: () => api.get<Vm>(`/v1/vms/${id}`),
    enabled: !!id,
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useVmHealth(id: string | undefined) {
  return useQuery<VmHealthPoint[]>({
    queryKey: ['vms', id, 'health'],
    queryFn: () => api.get<{ data: VmHealthPoint[] }>(`/v1/vms/${id}/health`).then((r) => r.data ?? []),
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

export function useVmActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['vms'] });

  const pause = useMutation({
    mutationFn: (id: string) => api.post(`/v1/vms/${id}/pause`),
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: (id: string) => api.post(`/v1/vms/${id}/resume`),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/vms/${id}`),
    onSuccess: invalidate,
  });
  const create = useMutation({
    mutationFn: (body: { slug: string; base_url: string; token: string; region?: string; weight?: number; max_concurrent?: number }) =>
      api.post<Vm>('/v1/vms', body),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Vm> }) => api.patch<Vm>(`/v1/vms/${id}`, body),
    onSuccess: invalidate,
  });
  const rotateToken = useMutation({
    mutationFn: ({ id, token }: { id: string; token: string }) =>
      api.post<{ ok: true }>(`/v1/vms/${id}/rotate-token`, { token }),
    onSuccess: invalidate,
  });
  const testConnection = useMutation({
    mutationFn: (body: { base_url: string; token: string }) =>
      api.post<{ ok: boolean; details?: string }>('/v1/vms/test-connection', body),
  });
  const probe = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; status?: number; details?: string }>(`/v1/vms/${id}/probe`),
  });

  return { pause, resume, remove, create, update, rotateToken, testConnection, probe };
}

export interface JobsQuery {
  source?: string;
  status?: string;
  vm?: string;
  since?: string;
  location_kind?: string;
  [k: string]: string | undefined;
}

export function useJobs(filters: JobsQuery = {}) {
  return useQuery<Job[]>({
    queryKey: ['jobs', filters],
    queryFn: () => api.get<{ jobs: Job[] }>('/v1/jobs', filters).then((r) => r.jobs ?? []),
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useJob(id: string | undefined) {
  return useQuery<Job>({
    queryKey: ['jobs', id],
    queryFn: () => api.get<Job>(`/v1/jobs/${id}`),
    enabled: !!id,
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useJobEvents(id: string | undefined) {
  return useQuery<JobEvent[]>({
    queryKey: ['jobs', id, 'events'],
    queryFn: () => api.get<{ events: JobEvent[] }>(`/v1/jobs/${id}/events`).then((r) => r.events ?? []),
    enabled: !!id,
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useJobContacts(id: string | undefined) {
  return useQuery<Contact[]>({
    queryKey: ['jobs', id, 'contacts'],
    queryFn: () => api.get<{ contacts: Contact[] }>(`/v1/jobs/${id}/contacts`).then((r) => r.contacts ?? []),
    enabled: !!id,
  });
}

export function useBatches() {
  return useQuery<Batch[]>({
    queryKey: ['batches'],
    queryFn: () => api.get<{ batches: Batch[] }>('/v1/batches').then((r) => r.batches ?? []),
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useBatch(id: string | undefined) {
  return useQuery<Batch & { jobs: Job[] }>({
    queryKey: ['batches', id],
    queryFn: () => api.get(`/v1/batches/${id}`),
    enabled: !!id,
  });
}

export function useBatchActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['batches'] });
    qc.invalidateQueries({ queryKey: ['jobs'] });
  };
  return {
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/v1/batches/${id}`),
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: (id: string) => api.post(`/v1/batches/${id}/cancel`),
      onSuccess: invalidate,
    }),
  };
}

export function useJobActions() {
  const qc = useQueryClient();
  return {
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/v1/jobs/${id}`),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
    }),
  };
}

export function useContacts(query: string) {
  return useQuery<Contact[]>({
    queryKey: ['contacts', query],
    queryFn: () => api.get<{ contacts: Contact[] }>('/v1/contacts', { q: query }).then((r) => r.contacts ?? []),
  });
}

export function useContact(id: string | undefined) {
  return useQuery<Contact>({
    queryKey: ['contacts', id],
    queryFn: () => api.get<Contact>(`/v1/contacts/${id}`),
    enabled: !!id,
  });
}

export function useContactOutboundPushes(contactId: string | undefined) {
  return useQuery<OutboundPush[]>({
    queryKey: ['contacts', contactId, 'outbound_pushes'],
    queryFn: () =>
      api
        .get<{ outbound_pushes: OutboundPush[] }>(`/v1/contacts/${contactId}/outbound_pushes`)
        .then((r) => r.outbound_pushes ?? []),
    enabled: !!contactId,
  });
}

export function useOutboundPushes(
  filters: { target?: string; status?: string; limit?: number; cursor?: string } = {},
) {
  return useQuery<OutboundPush[]>({
    queryKey: ['outbound_pushes', filters],
    queryFn: () =>
      api
        .get<{ outbound_pushes: OutboundPush[] }>('/v1/outbound_pushes', filters)
        .then((r) => r.outbound_pushes ?? []),
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useFormFills(filters: { status?: string } = {}) {
  return useQuery<FormFill[]>({
    queryKey: ['form_fills', filters],
    queryFn: () => api.get<{ form_fills: FormFill[] }>('/v1/form_fills', filters).then((r) => r.form_fills ?? []),
    refetchInterval: LIVE_INTERVAL,
  });
}

export function useAudit(since?: string) {
  return useQuery<AuditEntry[]>({
    queryKey: ['audit', since],
    queryFn: () => api.get<{ entries: AuditEntry[] }>('/v1/audit', { since }).then((r) => r.entries ?? []),
    refetchInterval: 30_000,
  });
}

export function useIngressKeys() {
  return useQuery<IngressKey[]>({
    queryKey: ['ingress_keys'],
    queryFn: () => api.get<{ keys: IngressKey[] }>('/v1/admin/ingress-keys').then((r) => r.keys ?? []),
  });
}

export function useIngressKeyActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['ingress_keys'] });
  return {
    create: useMutation({
      mutationFn: (body: { name: string; scopes: string[] }) =>
        api.post<{ key: IngressKey; secret: string }>('/v1/admin/ingress-keys', body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/v1/admin/ingress-keys/${id}`),
      onSuccess: invalidate,
    }),
  };
}

export function useSecrets() {
  return useQuery<ServiceSecret[]>({
    queryKey: ['secrets'],
    queryFn: () => api.get<{ secrets: ServiceSecret[] }>('/v1/admin/secrets').then((r) => r.secrets ?? []),
  });
}

export function useSecretActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['secrets'] });
  return {
    upsert: useMutation({
      mutationFn: (body: { name: string; service: string; value: string; kind?: string }) =>
        api.post<ServiceSecret>('/v1/admin/secrets', { ...body, kind: body.kind ?? body.name }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/v1/admin/secrets/${id}`),
      onSuccess: invalidate,
    }),
  };
}

export interface DomainsQuery {
  workspace_id: string;
  provider?: string;
  country?: string;
  status?: string;
  warmup?: string;
  limit?: number;
  offset?: number;
}

export function useDomains(q: DomainsQuery) {
  return useQuery<DomainsResponse>({
    queryKey: ['domains', q],
    queryFn: () => api.get<DomainsResponse>('/v1/domains', q as unknown as Record<string, string | number>),
    enabled: !!q.workspace_id,
  });
}

export function useDomainDetail(id: string | null, workspaceId: string) {
  return useQuery<DomainDetail>({
    queryKey: ['domains', id],
    queryFn: () =>
      api.get<DomainDetail>(`/v1/domains/${id}`, { workspace_id: workspaceId }),
    enabled: !!id && !!workspaceId,
  });
}

export function useDomainActions(workspaceId: string) {
  const qc = useQueryClient();
  const invalidateDomains = () => qc.invalidateQueries({ queryKey: ['domains'] });

  const recheckDns = useMutation({
    mutationFn: (domainId: string) =>
      api.post<{ task_id: string }>(
        `/v1/domains/${domainId}/recheck-dns?workspace_id=${workspaceId}`,
      ),
    onSuccess: invalidateDomains,
  });

  const configureForwarder = useMutation({
    mutationFn: ({ domainId, target }: { domainId: string; target: string }) =>
      api.post(`/v1/domains/${domainId}/forwarder/configure`, {
        workspace_id: workspaceId,
        ionos_forwarder_target: target,
      }),
    onSuccess: invalidateDomains,
  });

  const addRedirect = useMutation({
    mutationFn: ({
      domainId,
      target_url,
      valid_until,
      override,
    }: {
      domainId: string;
      target_url: string;
      valid_until?: string;
      override?: boolean;
    }) =>
      api.post(`/v1/domains/${domainId}/redirects`, {
        workspace_id: workspaceId,
        target_url,
        valid_until,
        override,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['domains', vars.domainId] });
    },
  });

  return { recheckDns, configureForwarder, addRedirect };
}

// ── Email accounts ────────────────────────────────────────────────────────────

export function useEmailAccounts(q: EmailAccountsQuery) {
  return useQuery<EmailAccountsListResponse>({
    queryKey: ['email-accounts', q],
    queryFn: () => fetchEmailAccounts(q),
    enabled: !!q.workspace_id,
  });
}

export function useEmailAccountDetail(id: string | null, workspaceId: string) {
  return useQuery<EmailAccountDetail>({
    queryKey: ['email-accounts', id],
    queryFn: () => fetchEmailAccount(id!, workspaceId),
    enabled: !!id && !!workspaceId,
  });
}

export function useEmailAccountActions(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['email-accounts'] });

  return {
    pause: useMutation({
      mutationFn: (id: string) => pauseEmailAccount(id, workspaceId),
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: (id: string) => resumeEmailAccount(id, workspaceId),
      onSuccess: invalidate,
    }),
    detach: useMutation({
      mutationFn: (id: string) => detachEmailAccount(id, workspaceId),
      onSuccess: invalidate,
    }),
    replaceGroups: useMutation({
      mutationFn: ({ id, group_ids }: { id: string; group_ids: string[] }) =>
        replaceEmailAccountGroups(id, workspaceId, group_ids),
      onSuccess: invalidate,
    }),
    sync: useMutation({
      mutationFn: () => syncEmailAccounts(workspaceId),
    }),
  };
}

export function useMailAccountGroups(workspaceId: string) {
  return useQuery<MailAccountGroupsResponse>({
    queryKey: ['mail-account-groups', workspaceId],
    queryFn: () => fetchMailAccountGroups(workspaceId),
    enabled: !!workspaceId,
  });
}

export function useMailAccountGroupActions(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['mail-account-groups', workspaceId] });

  return {
    create: useMutation({
      mutationFn: ({ name, color }: { name: string; color?: string }) =>
        createMailAccountGroup(workspaceId, name, color),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...fields }: { id: string; name?: string; color?: string }) =>
        updateMailAccountGroup(id, workspaceId, fields),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => deleteMailAccountGroup(id, workspaceId),
      onSuccess: invalidate,
    }),
    bulkAdd: useMutation({
      mutationFn: ({ groupId, account_ids }: { groupId: string; account_ids: string[] }) =>
        bulkAddToGroup(groupId, workspaceId, account_ids),
      onSuccess: invalidate,
    }),
    bulkRemove: useMutation({
      mutationFn: ({ groupId, account_ids }: { groupId: string; account_ids: string[] }) =>
        bulkRemoveFromGroup(groupId, workspaceId, account_ids),
      onSuccess: invalidate,
    }),
  };
}

// ── Label mappings ─────────────────────────────────────────────────────────

export interface LabelMappingsQuery {
  workspace_id: string;
  provider?: LabelMappingProvider;
  active?: boolean;
}

export function useLabelMappings(query: LabelMappingsQuery) {
  return useQuery<LabelMappingsResponse>({
    queryKey: ['label-mappings', query],
    queryFn: () =>
      api.get<LabelMappingsResponse>('/v1/label-mappings', query as unknown as Record<string, string | boolean>),
    enabled: !!query.workspace_id,
  });
}

export interface CreateLabelMappingBody {
  workspace_id: string;
  provider: LabelMappingProvider;
  successai_label_id: string;
  successai_label_name?: string;
  mapped_event: LabelMappedEvent;
  active?: boolean;
}

export interface UpdateLabelMappingBody {
  workspace_id: string;
  provider?: LabelMappingProvider;
  successai_label_id?: string;
  successai_label_name?: string;
  mapped_event?: LabelMappedEvent;
  active?: boolean;
}

export function useLabelMappingActions(workspaceId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['label-mappings'] });

  const create = useMutation({
    mutationFn: (body: CreateLabelMappingBody) =>
      api.post<{ item: LabelMapping }>('/v1/label-mappings', body),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }: UpdateLabelMappingBody & { id: string }) =>
      api.patch<{ item: LabelMapping }>(`/v1/label-mappings/${id}`, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/v1/label-mappings/${id}?workspace_id=${encodeURIComponent(workspaceId)}`),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
